/**
 * Layout-model van het Mandala-wiel: vertaalt de bevestigde poortvolgorde + het
 * graden-anker naar concrete SVG-segmenten (poorten, lijnen, dierenriem,
 * kwartalen) met stabiele ID's en berekende paden.
 *
 * Richting-conventie (HD-specifiek): toenemende zodiac-lengte loopt **tegen de
 * klok in** (CCW) op het scherm. Dit is hét enige punt waar de CCW-handedness
 * wordt vastgelegd, via {@link longitudeToScreenAngle}. De grens tussen poort 10
 * en 11 valt exact op de top (12 uur).
 */

import {
  DEFAULT_MANDALA_GEOMETRY,
  MANDALA_DEGREES_PER_GATE,
  MANDALA_DEGREES_PER_LINE,
  MANDALA_DEGREES_PER_ZODIAC_SIGN,
  MANDALA_GATE_41_START_LONGITUDE,
  MANDALA_GATE_SEQUENCE_CCW,
  MANDALA_GODHEADS,
  MANDALA_GODHEAD_GATE_SPAN,
  MANDALA_LINES_PER_GATE,
  MANDALA_QUARTERS,
  MANDALA_QUARTER_GATE_SPAN,
  MANDALA_ZODIAC_SIGNS,
  MandalaGeometryConfig,
  MandalaRingRadii,
} from './mandala.constants';
import { describeAnnularSector, normalizeAngle, polarToCartesian } from './mandala-geometry.utils';

const FULL_CIRCLE = 360;

const GATE_INDEX_BY_NUMBER: ReadonlyMap<number, number> = new Map(
  MANDALA_GATE_SEQUENCE_CCW.map((gate, index) => [gate, index]),
);

const GATE_41_SEQUENCE_INDEX = GATE_INDEX_BY_NUMBER.get(41) ?? -1;

/**
 * Absolute zodiac-lengte (graden, [0, 360)) waarop de top van het wiel (12 uur)
 * ligt. Afgeleid: de grens poort 10/11 = start van poort 10.
 */
export const MANDALA_WHEEL_TOP_LONGITUDE = gateStartLongitude(10);

export interface MandalaSegmentGeometry {
  readonly startAngle: number;
  readonly endAngle: number;
  readonly midAngle: number;
  readonly innerRadius: number;
  readonly outerRadius: number;
  readonly pathD: string;
}

export interface MandalaGateSegment extends MandalaSegmentGeometry {
  readonly gate: number;
  readonly id: string;
  readonly startLongitude: number;
  readonly endLongitude: number;
}

export interface MandalaLineSegment extends MandalaSegmentGeometry {
  readonly gate: number;
  readonly line: number;
  readonly id: string;
  readonly startLongitude: number;
  readonly endLongitude: number;
}

export interface MandalaZodiacSegment extends MandalaSegmentGeometry {
  readonly key: string;
  readonly name: string;
  readonly glyph: string;
  readonly id: string;
  readonly startLongitude: number;
  readonly endLongitude: number;
}

export interface MandalaQuarterSegment extends MandalaSegmentGeometry {
  readonly key: string;
  readonly label: string;
  readonly purpose: string;
  readonly star: string;
  readonly title: string;
  readonly id: string;
  readonly gates: readonly number[];
  readonly startLongitude: number;
  readonly endLongitude: number;
}

export interface MandalaGodheadSegment extends MandalaSegmentGeometry {
  readonly key: string;
  readonly name: string;
  readonly quarterKey: string;
  readonly id: string;
  readonly gates: readonly number[];
  readonly startLongitude: number;
  readonly endLongitude: number;
}

export interface MandalaIchingSymbol {
  readonly gate: number;
  readonly id: string;
  readonly midAngle: number;
  readonly glyphX: number;
  readonly glyphY: number;
}

/** Index van een poort in de CCW-volgorde (0..63), of -1 indien onbekend. */
export function gateSequenceIndex(gate: number): number {
  return GATE_INDEX_BY_NUMBER.get(gate) ?? -1;
}

/**
 * Absolute zodiac-lengte (graden, [0, 360)) waarop een poort start, afgeleid uit
 * het anker (poort 41 = 302°) en de CCW-volgorde.
 */
export function gateStartLongitude(gate: number): number {
  const index = GATE_INDEX_BY_NUMBER.get(gate);
  if (index == null) {
    return Number.NaN;
  }
  const offset = (index - GATE_41_SEQUENCE_INDEX) * MANDALA_DEGREES_PER_GATE;
  return normalizeLongitude(MANDALA_GATE_41_START_LONGITUDE + offset);
}

/** Absolute zodiac-lengte (graden, [0, 360)) waarop een lijn (1..6) start. */
export function lineStartLongitude(gate: number, line: number): number {
  return normalizeLongitude(gateStartLongitude(gate) + (line - 1) * MANDALA_DEGREES_PER_LINE);
}

/** Volledige HD-activatie van een lengte: poort.lijn.kleur.toon.basis. */
export interface MandalaActivation {
  readonly gate: number;
  readonly line: number;
  readonly color: number;
  readonly tone: number;
  readonly base: number;
}

/**
 * Absolute zodiac-lengte → poort.lijn.kleur.toon.basis. Zelfde rekenwijze als de backend
 * (`GateLineMapper`): per niveau afronden naar beneden binnen de vorige eenheid (kleur = lijn/6,
 * toon = kleur/6, basis = toon/5). Gebruikt voor de tooltips van de ephemeris-pagina.
 */
export function activationFromLongitude(longitude: number): MandalaActivation {
  const offsetFrom41 = normalizeLongitude(longitude - MANDALA_GATE_41_START_LONGITUDE);
  const gateSteps = Math.min(Math.floor(offsetFrom41 / MANDALA_DEGREES_PER_GATE), MANDALA_GATE_SEQUENCE_CCW.length - 1);
  const gate = MANDALA_GATE_SEQUENCE_CCW[(GATE_41_SEQUENCE_INDEX + gateSteps) % MANDALA_GATE_SEQUENCE_CCW.length];

  const colorWidth = MANDALA_DEGREES_PER_LINE / 6;
  const toneWidth = colorWidth / 6;
  const baseWidth = toneWidth / 5;
  const step = (rest: number, width: number, max: number): number => Math.min(Math.max(Math.floor(rest / width) + 1, 1), max);

  const inGate = offsetFrom41 - gateSteps * MANDALA_DEGREES_PER_GATE;
  const line = step(inGate, MANDALA_DEGREES_PER_LINE, MANDALA_LINES_PER_GATE);
  const inLine = inGate - (line - 1) * MANDALA_DEGREES_PER_LINE;
  const color = step(inLine, colorWidth, 6);
  const inColor = inLine - (color - 1) * colorWidth;
  const tone = step(inColor, toneWidth, 6);
  const inTone = inColor - (tone - 1) * toneWidth;
  const base = step(inTone, baseWidth, 5);
  return { gate, line, color, tone, base };
}

/**
 * Mapt een absolute zodiac-lengte naar een schermhoek (graden, met de klok mee
 * vanaf de top — zie `mandala-geometry.utils`). Toenemende lengte → afnemende
 * schermhoek = **counterclockwise** op het scherm.
 */
export function longitudeToScreenAngle(longitude: number): number {
  return normalizeAngle(MANDALA_WHEEL_TOP_LONGITUDE - longitude);
}

function normalizeLongitude(longitude: number): number {
  return ((longitude % FULL_CIRCLE) + FULL_CIRCLE) % FULL_CIRCLE;
}

/**
 * Berekent de schermhoeken + pad voor een segment dat in toenemende lengte van
 * `startLongitude` over `spanDegrees` loopt. Omdat toenemende lengte CCW is,
 * wordt het segment genormaliseerd zodat `endAngle = startAngle + span` (de
 * annular-sector-helper vereist `endAngle >= startAngle`).
 */
function buildSegmentGeometry(
  startLongitude: number,
  spanDegrees: number,
  ring: MandalaRingRadii,
  geometry: MandalaGeometryConfig,
): MandalaSegmentGeometry {
  const startAngle = longitudeToScreenAngle(startLongitude + spanDegrees);
  const endAngle = startAngle + spanDegrees;
  const midAngle = startAngle + spanDegrees / 2;
  const { x, y } = geometry.center;
  return {
    startAngle,
    endAngle,
    midAngle,
    innerRadius: ring.innerRadius,
    outerRadius: ring.outerRadius,
    pathD: describeAnnularSector(x, y, ring.innerRadius, ring.outerRadius, startAngle, endAngle),
  };
}

export function buildMandalaGateSegments(
  geometry: MandalaGeometryConfig = DEFAULT_MANDALA_GEOMETRY,
): MandalaGateSegment[] {
  return MANDALA_GATE_SEQUENCE_CCW.map(gate => {
    const startLongitude = gateStartLongitude(gate);
    const geom = buildSegmentGeometry(startLongitude, MANDALA_DEGREES_PER_GATE, geometry.gateRing, geometry);
    return {
      gate,
      id: `wheel-gate-${gate}`,
      startLongitude,
      endLongitude: normalizeLongitude(startLongitude + MANDALA_DEGREES_PER_GATE),
      ...geom,
    };
  });
}

export function buildMandalaLineSegments(
  geometry: MandalaGeometryConfig = DEFAULT_MANDALA_GEOMETRY,
): MandalaLineSegment[] {
  const segments: MandalaLineSegment[] = [];
  for (const gate of MANDALA_GATE_SEQUENCE_CCW) {
    for (let line = 1; line <= MANDALA_LINES_PER_GATE; line++) {
      const startLongitude = lineStartLongitude(gate, line);
      const geom = buildSegmentGeometry(startLongitude, MANDALA_DEGREES_PER_LINE, geometry.lineRing, geometry);
      segments.push({
        gate,
        line,
        id: `wheel-gate-${gate}-line-${line}`,
        startLongitude,
        endLongitude: normalizeLongitude(startLongitude + MANDALA_DEGREES_PER_LINE),
        ...geom,
      });
    }
  }
  return segments;
}

export function buildMandalaZodiacSegments(
  geometry: MandalaGeometryConfig = DEFAULT_MANDALA_GEOMETRY,
): MandalaZodiacSegment[] {
  return MANDALA_ZODIAC_SIGNS.map(sign => {
    const geom = buildSegmentGeometry(
      sign.startLongitude,
      MANDALA_DEGREES_PER_ZODIAC_SIGN,
      geometry.zodiacRing,
      geometry,
    );
    return {
      key: sign.key,
      name: sign.name,
      glyph: sign.glyph,
      id: `wheel-sign-${sign.key}`,
      startLongitude: sign.startLongitude,
      endLongitude: normalizeLongitude(sign.startLongitude + MANDALA_DEGREES_PER_ZODIAC_SIGN),
      ...geom,
    };
  });
}

export function buildMandalaQuarterSegments(
  geometry: MandalaGeometryConfig = DEFAULT_MANDALA_GEOMETRY,
): MandalaQuarterSegment[] {
  const spanDegrees = MANDALA_QUARTER_GATE_SPAN * MANDALA_DEGREES_PER_GATE;
  return MANDALA_QUARTERS.map(quarter => {
    const gates = MANDALA_GATE_SEQUENCE_CCW.slice(
      quarter.sequenceStartIndex,
      quarter.sequenceStartIndex + MANDALA_QUARTER_GATE_SPAN,
    );
    const startLongitude = gateStartLongitude(gates[0]);
    const geom = buildSegmentGeometry(startLongitude, spanDegrees, geometry.quarterRing, geometry);
    return {
      key: quarter.key,
      label: quarter.label,
      purpose: quarter.purpose,
      star: quarter.star,
      title: quarter.title,
      id: `wheel-quarter-${quarter.key}`,
      gates,
      startLongitude,
      endLongitude: normalizeLongitude(startLongitude + spanDegrees),
      ...geom,
    };
  });
}

/**
 * Segmenten van de godhead-band: 16 godheads van elk 4 poorten (22,5°),
 * conform "The Faces of the Godhead". Zelfde afleiding als de kwartalen —
 * startlengte van de eerste poort + vaste span — op de godhead-ring-radii.
 */
export function buildMandalaGodheadSegments(
  geometry: MandalaGeometryConfig = DEFAULT_MANDALA_GEOMETRY,
): MandalaGodheadSegment[] {
  const spanDegrees = MANDALA_GODHEAD_GATE_SPAN * MANDALA_DEGREES_PER_GATE;
  return MANDALA_GODHEADS.map(godhead => {
    const gates = MANDALA_GATE_SEQUENCE_CCW.slice(
      godhead.sequenceStartIndex,
      godhead.sequenceStartIndex + MANDALA_GODHEAD_GATE_SPAN,
    );
    const startLongitude = gateStartLongitude(gates[0]);
    const geom = buildSegmentGeometry(startLongitude, spanDegrees, geometry.godheadRing, geometry);
    return {
      key: godhead.key,
      name: godhead.name,
      quarterKey: godhead.quarterKey,
      id: `wheel-godhead-${godhead.key}`,
      gates,
      startLongitude,
      endLongitude: normalizeLongitude(startLongitude + spanDegrees),
      ...geom,
    };
  });
}

/**
 * Posities (één per poort) voor de I-Tjing-hexagram-glyphs, gecentreerd op de
 * poort-midden-hoek op de straal van {@link MandalaGeometryConfig.ichingRing}.
 * Het glyph-karakter zelf wordt door de render-component afgeleid uit het
 * poortnummer (King Wen-volgorde).
 */
export function buildMandalaIchingSymbols(
  geometry: MandalaGeometryConfig = DEFAULT_MANDALA_GEOMETRY,
): MandalaIchingSymbol[] {
  const { x: cx, y: cy } = geometry.center;
  const radius = (geometry.ichingRing.innerRadius + geometry.ichingRing.outerRadius) / 2;
  return MANDALA_GATE_SEQUENCE_CCW.map(gate => {
    const startLongitude = gateStartLongitude(gate);
    const midAngle =
      longitudeToScreenAngle(startLongitude + MANDALA_DEGREES_PER_GATE) + MANDALA_DEGREES_PER_GATE / 2;
    const pos = polarToCartesian(cx, cy, radius, midAngle);
    return {
      gate,
      id: `wheel-iching-${gate}`,
      midAngle,
      glyphX: pos.x,
      glyphY: pos.y,
    };
  });
}
